import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Spinner } from '../components/ui/Spinner';
import { PasswordField, isPasswordValid } from '../components/auth/PasswordField';

type View = 'request' | 'sent' | 'update' | 'done';

const RATE_LIMIT_KEY = 'professya_reset_attempts';
const MAX_ATTEMPTS   = 3;
const WINDOW_MS      = 3_600_000; // 1 hour
const COOLDOWN_MS    = 300_000;   // 5 minutes between sends

function checkResetRateLimit(setError: (msg: string) => void): boolean {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_KEY);
    const data   = stored ? JSON.parse(stored) : { count: 0, resetAt: 0, lastSentAt: 0 };
    const now    = Date.now();

    // Reset window if expired
    if (now > data.resetAt) {
      localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify({ count: 1, resetAt: now + WINDOW_MS, lastSentAt: now }));
      return true;
    }

    // Cooldown between sends
    const cooldownLeft = data.lastSentAt + COOLDOWN_MS - now;
    if (cooldownLeft > 0) {
      const mins = Math.ceil(cooldownLeft / 60_000);
      setError(`Aguarde ${mins} minuto(s) antes de solicitar outro e-mail.`);
      return false;
    }

    // Max attempts per window
    if (data.count >= MAX_ATTEMPTS) {
      const minsLeft = Math.ceil((data.resetAt - now) / 60_000);
      setError(`Muitas tentativas. Aguarde ${minsLeft} minuto(s).`);
      return false;
    }

    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify({ ...data, count: data.count + 1, lastSentAt: now }));
    return true;
  } catch {
    return true; // localStorage unavailable, fail open
  }
}

export function ResetPassword() {
  const navigate = useNavigate();
  const [view,         setView]         = useState<View>('request');
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [passwordOk,   setPasswordOk]   = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [cooldownSecs, setCooldownSecs] = useState(0);

  // Detect password-recovery session from URL hash
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setView('update');
    });
    return () => subscription.unsubscribe();
  }, []);

  // Cooldown countdown timer
  useEffect(() => {
    if (cooldownSecs <= 0) return;
    const id = setTimeout(() => setCooldownSecs(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldownSecs]);

  async function handleRequest(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!checkResetRateLimit(setError)) return;

    setLoading(true);

    // RT-06: Always show the same success message regardless of whether email exists.
    // This prevents email enumeration via password reset.
    await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    // Do NOT check authError, always move to 'sent' view to prevent enumeration.

    setLoading(false);
    setCooldownSecs(COOLDOWN_MS / 1000);
    setView('sent');
  }

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!isPasswordValid(password)) return;

    setLoading(true);
    setError(null);

    const { error: updateError } = await supabase.auth.updateUser({ password });

    setLoading(false);
    if (updateError) {
      setError('Não foi possível atualizar a senha. Tente solicitar um novo link.');
      return;
    }

    setView('done');
    // Auto-navigate to dashboard after 2 seconds (already logged in)
    setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--bg-base)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'inline-block' }}>
            <img src="/logo.svg" alt="Professya" style={{ height: '44px', width: 'auto' }} />
          </Link>
        </div>

        <div className="card">

          {/* ── Request link ── */}
          {view === 'request' && (
            <form onSubmit={handleRequest} noValidate>
              <h2 className="font-display" style={{ fontSize: '1.25rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                Recuperar senha
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.5rem' }}>
                Informe seu e-mail e enviaremos um link para redefinir sua senha.
              </p>

              {error && (
                <div role="alert" style={{
                  padding: '0.875rem 1rem', backgroundColor: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-md)',
                  color: '#fca5a5', fontSize: 'var(--text-label)', marginBottom: '1.25rem',
                }}>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label className="form-label" htmlFor="email">E-mail</label>
                <input
                  id="email"
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  autoComplete="email"
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading || !email}>
                {loading ? <Spinner size="sm" /> : 'Enviar link de recuperação'}
              </button>

              <p style={{ textAlign: 'center', marginTop: '1.25rem' }}>
                <Link to="/login" style={{ fontSize: 'var(--text-label)', color: 'var(--text-tertiary)' }}>
                  ← Voltar ao login
                </Link>
              </p>
            </form>
          )}

          {/* ── Email sent ── */}
          {view === 'sent' && (
            <div style={{ textAlign: 'center' }}>
              <span style={{ fontSize: '2.75rem', display: 'block', marginBottom: '1rem' }}>📬</span>
              <h2 className="font-display" style={{ fontSize: '1.25rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                E-mail enviado!
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.5rem' }}>
                {/* RT-06: always same message, never confirms if email is registered */}
                Se este email tiver uma conta, você receberá as instruções em breve.
              </p>

              {error && (
                <div role="alert" style={{
                  padding: '0.75rem 1rem', backgroundColor: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-md)',
                  color: '#fca5a5', fontSize: 'var(--text-label)', marginBottom: '1rem',
                }}>
                  {error}
                </div>
              )}

              <button
                type="button"
                className="btn btn-secondary btn-block"
                style={{ marginBottom: '0.75rem' }}
                disabled={cooldownSecs > 0 || loading}
                onClick={() => { setView('request'); setError(null); }}
              >
                {cooldownSecs > 0
                  ? `Reenviar em ${cooldownSecs}s`
                  : 'Reenviar e-mail'}
              </button>

              <Link to="/login" className="btn btn-secondary btn-block" style={{ display: 'block' }}>
                Voltar ao login
              </Link>
            </div>
          )}

          {/* ── Update password ── */}
          {view === 'update' && (
            <form onSubmit={handleUpdate} noValidate>
              <h2 className="font-display" style={{ fontSize: '1.25rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                Criar nova senha
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.5rem' }}>
                Escolha uma senha segura para sua conta.
              </p>

              {error && (
                <div role="alert" style={{
                  padding: '0.875rem 1rem', backgroundColor: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-md)',
                  color: '#fca5a5', fontSize: 'var(--text-label)', marginBottom: '1.25rem',
                }}>
                  {error}
                </div>
              )}

              <PasswordField
                id="new-password"
                label="Nova senha"
                value={password}
                onChange={setPassword}
                showChecklist
                autoComplete="new-password"
                onValidChange={setPasswordOk}
              />

              <button
                type="submit"
                className="btn btn-primary btn-block"
                style={{ marginTop: '0.5rem' }}
                disabled={loading || !passwordOk}
              >
                {loading ? <Spinner size="sm" /> : 'Salvar nova senha'}
              </button>
            </form>
          )}

          {/* ── Done (auto-login) ── */}
          {view === 'done' && (
            <div style={{ textAlign: 'center' }}>
              <span style={{ fontSize: '2.75rem', display: 'block', marginBottom: '1rem' }}>✅</span>
              <h2 className="font-display" style={{ fontSize: '1.25rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                Senha atualizada!
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
                Redirecionando para o painel...
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
